import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import * as Sentry from '@sentry/react'
import { Mutex } from 'async-mutex'
import i18n from 'i18next'

import { expireSession } from './auth/actions'
import { openCreditsTopUp } from './creditsTopUp/creditsTopUpSlice'
import { pushErrorNotification } from './notifications/actions'

const baseQuery = fetchBaseQuery({ baseUrl: '/' })

const mutex = new Mutex()

const OWNERSHIP_ERROR_PATTERN = /does not own this .+ account/i

const SELF_REPORTING_ENDPOINTS = new Set(['generateImages'])

const INSUFFICIENT_CREDITS_PATTERN = /insufficient credits/i

function isInsufficientCreditsError(error: FetchBaseQueryError): boolean {
  const message = (error.data as { message?: unknown } | undefined)?.message
  return (
    error.status === 402 &&
    typeof message === 'string' &&
    INSUFFICIENT_CREDITS_PATTERN.test(message)
  )
}

export function getOwnershipErrorMessage(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('status' in error)) return undefined
  const { status, data } = error as FetchBaseQueryError
  const message = (data as { message?: string })?.message
  if (status === 500 && typeof message === 'string' && OWNERSHIP_ERROR_PATTERN.test(message)) {
    return message
  }
  return undefined
}

function intercept(api: BaseQueryApi, error: FetchBaseQueryError | undefined): void {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    if (getOwnershipErrorMessage(error) !== undefined) {
      return
    }

    if (isInsufficientCreditsError(error)) {
      api.dispatch(
        pushErrorNotification({
          message: i18n.t('notifications.credits-insufficient'),
          extra: {
            action: { label: i18n.t('notifications.top-up-credits'), dispatch: openCreditsTopUp() },
          },
        }),
      )

      return
    }

    if (error.status === 402) {
      api.dispatch(
        pushErrorNotification({
          message: i18n.t('notifications.subscription-expired'),
          extra: {
            action: { label: i18n.t('notifications.upgrade-now'), path: '/settings/' },
          },
        }),
      )

      return
    }

    if (SELF_REPORTING_ENDPOINTS.has(api.endpoint)) {
      return
    }

    if (error.status === 400) {
      const message = (error.data as { message?: string })?.message ?? i18n.t('common.error')
      api.dispatch(
        pushErrorNotification({
          message,
        }),
      )
    } else {
      api.dispatch(
        pushErrorNotification({
          message: i18n.t('common.error'),
        }),
      )
    }
  }
}

const baseQueryWithAutoRefetch: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock()

  let result = await baseQuery(args, api, extraOptions)

  if ('error' in result) {
    const status = result.error?.status

    if (status === 401 || status === 403) {
      if (!mutex.isLocked()) {
        const release = await mutex.acquire()

        try {
          const refreshResult = await baseQuery(
            { url: 'auth/refresh', method: 'POST' },
            api,
            extraOptions,
          )

          if ('error' in refreshResult) {
            api.dispatch(expireSession())
          } else {
            result = await baseQuery(args, api, extraOptions)
          }
        } finally {
          release()
        }
      } else {
        await mutex.waitForUnlock()
        result = await baseQuery(args, api, extraOptions)
      }
    } else {
      Sentry.captureMessage('API Error', {
        level: 'error',
        extra: {
          status: result.error?.status,
          response: result.error?.data,
          url: args,
        },
      })
      intercept(api, result.error)
    }
  }

  return result
}

const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAutoRefetch,
  tagTypes: [
    'Post',
    'Post-item',
    'User',
    'Post-statistics',
    'Post-templates',
    'Campaign',
    'UserNotificationsSettings',
    'Campaign-statistics',
    'Credits',
  ],
  endpoints: () => ({}),
})

export default api
